import React from 'react';
import { Grid, Group, Title, TextInput, Autocomplete, NumberInput, Select, Text, Switch, Paper } from '@mantine/core';
import type { ICotizacionNBS } from '../../../../modelos/CotizadorNBS/entidades/ICotizacionNBS';
import { OPCIONES_MESES_MANTENIMIENTO, OPCIONES_PLAZO_ENTREGA_MESES, OPCIONES_PLAZO_INSTALACION_MESES } from '../../interfaces';

const opcionesMeses = (meses: number[]) => meses.map(m => ({ value: String(m), label: `${m} meses` }));
const opcionesMesesMantenimiento = opcionesMeses(OPCIONES_MESES_MANTENIMIENTO);
const opcionesPlazoEntrega = opcionesMeses(OPCIONES_PLAZO_ENTREGA_MESES);
const opcionesPlazoInstalacion = opcionesMeses(OPCIONES_PLAZO_INSTALACION_MESES);
const unidadMeses = <Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>meses</Text>;

interface PasoProyectoVistaProps {
    cotizacion: ICotizacionNBS;
    alCambiarProyecto: (campo: any, valor: any) => void;
    errores: Record<string, string>;
    setErrores: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    ciudades: { value: string, label: string }[];
    estaAutoguardando: boolean;
    esAceptada: boolean;
    /** Más de 12 meses de mantenimiento solo con permiso (autorización de la gerencia comercial). */
    puedeExtenderMantenimiento: boolean;
    /** Solo en Panamá se puede elegir si la cotización lleva pólizas. */
    esPanama: boolean;
}

export const PasoProyectoVista: React.FC<PasoProyectoVistaProps> = ({
    cotizacion,
    alCambiarProyecto,
    errores,
    setErrores,
    ciudades,
    estaAutoguardando,
    esAceptada,
    puedeExtenderMantenimiento,
    esPanama
}) => {
    const cambiarMesesMantenimiento = (valor: string | number | null) => {
        alCambiarProyecto('MesesMantenimiento', Number(valor) || 0);
        setErrores(prev => ({ ...prev, MesesMantenimiento: '' }));
    };

    /** Plazos y garantías: vacío = null (sin diligenciar). */
    const cambiarMeses = (campo: 'PlazoEntregaMeses' | 'MesesInstalacion' | 'GarantiaFabricacionMeses' | 'GarantiaInstalacionMeses',
        valor: string | number | null) => {
        alCambiarProyecto(campo, valor === null || valor === '' ? null : Number(valor));
        setErrores(prev => ({ ...prev, [campo]: '' }));
    };

    return (
        <Grid>
            <Grid.Col span={12}>
                <Group justify="space-between" mb="md">
                    <Title order={5}>Información del Proyecto</Title>
                </Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                    size="xs"
                    withAsterisk
                    label="Cliente"
                    value={cotizacion.Proyecto.NombreCliente}
                    onChange={(e) => {
                        alCambiarProyecto('NombreCliente', e.currentTarget.value);
                        setErrores(prev => ({ ...prev, NombreCliente: '' }));
                    }}
                    error={errores.NombreCliente}
                    maxLength={200}
                    disabled={estaAutoguardando || esAceptada}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                    size="xs"
                    withAsterisk
                    label="Nombre del Proyecto"
                    value={cotizacion.Proyecto.Nombre}
                    onChange={(e) => {
                        alCambiarProyecto('Nombre', e.currentTarget.value);
                        setErrores(prev => ({ ...prev, Nombre: '' }));
                    }}
                    error={errores.Nombre}
                    maxLength={100}
                    disabled={estaAutoguardando || esAceptada}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <Autocomplete
                    size="xs"
                    withAsterisk
                    label="Ciudad"
                    data={ciudades || []}
                    value={cotizacion.Proyecto.Ciudad}
                    onChange={(val) => {
                        alCambiarProyecto('Ciudad', val || '');
                        setErrores(prev => ({ ...prev, Ciudad: '' }));
                    }}
                    error={errores.Ciudad}
                    disabled={estaAutoguardando || esAceptada}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <NumberInput min={0} allowNegative={false}
                    size="xs"
                    withAsterisk
                    label="Año"
                    value={cotizacion.Proyecto.Anio}
                    readOnly
                    error={errores.Anio}
                    disabled={estaAutoguardando || esAceptada}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <NumberInput min={0} allowNegative={false}
                    size="xs"
                    withAsterisk
                    label="Duración del Contrato"
                    rightSection={<Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>meses</Text>}
                    rightSectionWidth={50}
                    value={cotizacion.Proyecto.MesesContrato}
                    onChange={(val) => {
                        alCambiarProyecto('MesesContrato', Number(val));
                        setErrores(prev => ({ ...prev, MesesContrato: '' }));
                    }}
                    error={errores.MesesContrato}
                    disabled={estaAutoguardando || esAceptada}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                {/* 3, 6, 9 o 12 meses; con permiso se puede escribir cualquier valor (más de 12). */}
                {puedeExtenderMantenimiento ? (
                    <NumberInput min={OPCIONES_MESES_MANTENIMIENTO[0]} allowNegative={false} allowDecimal={false}
                        size="xs"
                        withAsterisk
                        label="Mantenimiento a costo"
                        description="3, 6, 9, 12 o más meses (más de 12 autorizado por tu rol)"
                        rightSection={<Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>meses</Text>}
                        rightSectionWidth={50}
                        value={cotizacion.Proyecto.MesesMantenimiento}
                        onChange={cambiarMesesMantenimiento}
                        error={errores.MesesMantenimiento}
                        disabled={estaAutoguardando || esAceptada}
                    />
                ) : (
                    <Select
                        size="xs"
                        withAsterisk
                        searchable
                        label="Mantenimiento a costo"
                        data={opcionesMesesMantenimiento}
                        value={OPCIONES_MESES_MANTENIMIENTO.includes(cotizacion.Proyecto.MesesMantenimiento) ? String(cotizacion.Proyecto.MesesMantenimiento) : null}
                        onChange={cambiarMesesMantenimiento}
                        error={errores.MesesMantenimiento}
                        disabled={estaAutoguardando || esAceptada}
                    />
                )}
            </Grid.Col>
            {/* Plazos y garantías de la Oferta Comercial (PDF): se diligencian en cada cotización. */}
            <Grid.Col span={12}>
                <Title order={5} mt="sm">Plazos y Garantías</Title>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Select size="xs" withAsterisk searchable label="Plazo de entrega"
                    data={opcionesPlazoEntrega}
                    value={cotizacion.Proyecto.PlazoEntregaMeses != null ? String(cotizacion.Proyecto.PlazoEntregaMeses) : null}
                    onChange={(v) => cambiarMeses('PlazoEntregaMeses', v)}
                    error={errores.PlazoEntregaMeses}
                    disabled={estaAutoguardando || esAceptada} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Select size="xs" withAsterisk searchable label="Plazo de instalación"
                    data={opcionesPlazoInstalacion}
                    value={cotizacion.Proyecto.MesesInstalacion != null ? String(cotizacion.Proyecto.MesesInstalacion) : null}
                    onChange={(v) => cambiarMeses('MesesInstalacion', v)}
                    error={errores.MesesInstalacion}
                    disabled={estaAutoguardando || esAceptada} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <NumberInput size="xs" withAsterisk hideControls min={1} max={255} allowNegative={false} allowDecimal={false}
                    label="Garantía de fabricación"
                    rightSection={unidadMeses} rightSectionWidth={50}
                    value={cotizacion.Proyecto.GarantiaFabricacionMeses ?? ''}
                    onChange={(v) => cambiarMeses('GarantiaFabricacionMeses', v)}
                    error={errores.GarantiaFabricacionMeses}
                    disabled={estaAutoguardando || esAceptada} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <NumberInput size="xs" withAsterisk hideControls min={1} max={255} allowNegative={false} allowDecimal={false}
                    label="Garantía de instalación"
                    rightSection={unidadMeses} rightSectionWidth={50}
                    value={cotizacion.Proyecto.GarantiaInstalacionMeses ?? ''}
                    onChange={(v) => cambiarMeses('GarantiaInstalacionMeses', v)}
                    error={errores.GarantiaInstalacionMeses}
                    disabled={estaAutoguardando || esAceptada} />
            </Grid.Col>
            {/* Solo Panamá: sección propia, como Plazos y Garantías. El porcentaje se edita en la pestaña Configuración. */}
            {esPanama && (
                <>
                    <Grid.Col span={12}>
                        <Title order={5} mt="sm">Pólizas</Title>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper withBorder radius="md" p="sm">
                            <Switch
                                size="sm"
                                label="Aplica pólizas"
                                description="Si no aplican, la cotización se calcula sin pólizas. El porcentaje se ajusta en la pestaña Configuración."
                                checked={cotizacion.Proyecto.AplicaPolizas}
                                onChange={(e) => alCambiarProyecto('AplicaPolizas', e.currentTarget.checked)}
                                disabled={estaAutoguardando || esAceptada}
                            />
                        </Paper>
                    </Grid.Col>
                </>
            )}
        </Grid>
    );
};
